import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {getApolloClient} from 'graphql-client/ApolloClient';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {postJSONPromise} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import type {McpServerToolsQuery, McpServerToolsQueryVariables} from './__generated/McpServerToolsQuery';
import {sharedStyles} from './McpModalStyles';

export const MCP_SERVER_TOOLS_QUERY = gql`
  query McpServerToolsQuery($serverId: StrictString!) @skipAudit @engComponent(name: LLM) {
    mcpServerTools(serverId: $serverId) {
      needsRedirect
      redirectUrl
      tools {
        name
        description
        parameters
      }
    }
  }
`;

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 minutes timeout

interface ConnectMCPModalProps {
  config: {
    id: string;
    name: string;
  };
  onRequestClose: () => void;
  onRequestSubmit: (userConfigId: string) => void;
}

export default function ConnectMCPModal(props: ConnectMCPModalProps): JSX.Element {
  const {config, onRequestClose, onRequestSubmit} = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false);
  const [authRedirectUrl, setAuthRedirectUrl] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const queryTools = useCallback(async (): Promise<McpServerToolsQuery['mcpServerTools']> => {
    const result = await getApolloClient().query<McpServerToolsQuery, McpServerToolsQueryVariables>({
      query: MCP_SERVER_TOOLS_QUERY,
      variables: {serverId: config.id},
      fetchPolicy: 'network-only',
    });
    return result.data.mcpServerTools;
  }, [config.id]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling]
  );

  const startPollingForAuth = useCallback(
    (userConfigId: string) => {
      const startTime = Date.now();

      pollingIntervalRef.current = setInterval(async () => {
        if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
          stopPolling();
          setIsWaitingForAuth(false);
          notifications.error('Authentication timed out. Please try again.');
          onRequestClose();
          return;
        }

        try {
          const toolsResult = await queryTools();
          if (!toolsResult.needsRedirect) {
            stopPolling();
            setIsWaitingForAuth(false);
            notifications.success(`${config.name} connected successfully`);
            onRequestSubmit(userConfigId);
          }
        } catch (error) {
          // Keep polling - user may still be authenticating
          if (error instanceof Error && !error.message.includes('Forbidden')) {
            throw error;
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [config.name, onRequestClose, onRequestSubmit, queryTools, stopPolling]
  );

  async function handleSubmit(): Promise<void> {
    try {
      setIsSubmitting(true);
      const response = await withLoadingScreen(
        postJSONPromise({
          url: '/1/api/llm/mcp-server-user-configs',
          data: {mcpServerConfigId: config.id, isEnabled: true},
        }),
        'Connecting to MCP Server...'
      );

      const userConfigId = response.id as string;
      const toolsResult = await queryTools();

      if (toolsResult.needsRedirect && toolsResult.redirectUrl) {
        window.open(toolsResult.redirectUrl, '_blank');
        setAuthRedirectUrl(toolsResult.redirectUrl);
        setIsWaitingForAuth(true);
        startPollingForAuth(userConfigId);
        return;
      }

      notifications.success(`${config.name} connected successfully`);
      onRequestSubmit(userConfigId);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isWaitingForAuth) {
    return (
      <Modal
        title="Waiting for authentication"
        onRequestClose={onRequestClose}
        buttons={[{text: 'Cancel', cancel: true}]}
      >
        <p className={css(styles.description)}>Complete authentication in the new tab, then return here.</p>
        <div className={css(styles.infoRow)}>
          <div className={css(styles.iconWrapper)}>
            <BenchlingIcon icon="refresh" className={css(sharedStyles.spinningIcon)} />
          </div>
          <div className={css(styles.infoContent)}>
            <p className={css(styles.infoTitle)}>Waiting for {config.name}...</p>
            <p className={css(styles.infoSubtext)}>This will update automatically once you authenticate</p>
          </div>
        </div>
        {authRedirectUrl && (
          <p className={css(styles.fallbackLink)}>
            Didn't get redirected?{' '}
            <a href={authRedirectUrl} target="_blank" rel="noopener noreferrer">
              Click here to open the authorization URL manually
            </a>
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title="Connect your account"
      onRequestSubmit={handleSubmit}
      onRequestClose={onRequestClose}
      buttons={[
        {text: 'Continue', submit: true, style: ButtonStyle.PRIMARY, disabled: isSubmitting},
        {text: 'Cancel', cancel: true},
      ]}
    >
      <p className={css(styles.description)}>
        Allow Benchling AI tools to access additional context on your behalf.
      </p>
      <div className={css(styles.infoRow)}>
        <div className={css(styles.iconWrapper)}>
          <BenchlingIcon icon="sequence" />
        </div>
        <div className={css(styles.infoContent)}>
          <p className={css(styles.infoTitle)}>{config.name} will open a new tab</p>
          <p className={css(styles.infoSubtext)}>Authenticate to connect your account</p>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  description: {
    marginBottom: 16,
    color: 'var(--text-primary)',
    fontSize: 13,
    lineHeight: '16px',
  },
  infoRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  },
  iconWrapper: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  infoTitle: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
    color: 'var(--text-primary)',
  },
  infoSubtext: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '16px',
    color: 'var(--text-primary)',
  },
  fallbackLink: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: '16px',
    color: 'var(--text-secondary)',
  },
});
